import VarType from '../enums/varType';
import { getAntiForgeryToken } from '../utils/getAntiForgeryToken';

/**
 * Collects accessToken and returns HTTP headers with collected token.
 */
function getHeadersWithAuthToken() {
    let headers = new Headers();
    const bearerToken = window.getTokenForHeader();
    // ENGINE-7502 Removed since never used and breaks authorization
    // headers.append('Authorization', bearerToken.Authorization);
    return headers;
}

/**
 * Checks if the given string is a valid JSON.
 */
function isValidJson(str) {
    try {
        JSON.parse(str);
        return true;
    } catch (e) {
        return false;
    }
}

// Set the request body and headers based on the body type.
export function setRequestBodyAndHeaders(body, options) {
    // If body is FormData, add as is. For plain objects, stringify and mark as JSON.
    if (body !== null && typeof body === VarType.Object && !(body instanceof Date)) {
        if (body instanceof FormData) {
            options.body = body;
        } else {
            options.body = JSON.stringify(body);
            options.headers.append('Content-Type', 'application/json');
        }
    }
    // If it's a valid JSON, use it as is, but set the 'Content-Type' to 'application/json'.
    else if (isValidJson(body)) {
        options.body = body;
        options.headers.append('Content-Type', 'application/json');
    }
    // If the body is a Date object, convert it to ISO string.
    else if (body instanceof Date) {
        options.body = body.toISOString();
    } else {
        options.body = body;
    }
}

/**
 * Returns a promise that finishes earliest after the given minimumMs.
 * @param {Object} promiseOrPromises
 * @param {number} minimumMs - defaults to 2000
 */
export function promiseForMinimumMs(promiseOrPromises, minimumMs = 2000) {
    const delayPromise = new Promise((resolve) => setTimeout(resolve, minimumMs));

    let promises = Array.isArray(promiseOrPromises)
        ? promiseOrPromises
        : promiseOrPromises ? [promiseOrPromises] : [];

    promises.push(delayPromise);

    return Promise.all(promises);
}

/**
 * Fetches data from the given apiUrl using devextreme-style filters.
 * @param apiUrl
 * @param filters devextreme-style filters, e.g. ['Name', 'contains', 'test']
 * @param skip number of entities skiped
 * @param take page size, if 0, all entities are fetched
 * @returns {Promise<any>}
 */
export function fetchEnData(apiUrl, filters = null, skip = 0, take = 0) {
    let getParams = {
        requireTotalCount: false,
        take: take,
        skip: skip,
        filter: filters ? JSON.stringify(filters) : null,
    };
    return fetch(apiUrl + '?' + new URLSearchParams(getParams), {
        method: 'GET',
    }).then(response => {
        if (!response.ok) {
            throw new Error(response.statusText);
        }
        return response.json();
    }).then(json => json.data);
}

/**
 * Build fetch request with corresponding params. Always contains Authorization token and XSRF
 * @param url request url
 * @param getParams json object to pass as get params
 * @param method GET/POST/PUT/DELETE. Default  - GET
 * @param body Object to send as request body. If it is json, corresponding Header will be passed automatically
 * @param headers Additional Headers if necessary
 * @param onSuccess Callback function that executes when request is successful
 * @param onError Callback function that executes if request fails
 * @param noResponseBody Flag, that indicates response should not be unwrapped
 * @returns {Promise<Response|*>} by default return request itself
 */
export async function enFetch({
                                  url,
                                  getParams,
                                  method = 'GET',
                                  body,
                                  signal,
                                  headers,
                                  onSuccess,
                                  onError,
                                  noResponseBody = false
                              }) {

    if (getParams != null) {
        url = url + '?' + new URLSearchParams(getParams);
    }
    // If body is null, nothing wrong will happen
    const options = {
        method: method,
        headers: getHeadersWithAuthToken(),
    };
    const afToken = getAntiForgeryToken();
    const onErrorIsFunction = typeof onError === VarType.Function;
    const onSuccessIsFunction = typeof onSuccess === VarType.Function;
    if (signal) {
        options.signal = signal;
    }
    setRequestBodyAndHeaders(body, options);

    if (afToken) {
        options.headers.append('XSRF-TOKEN', afToken);
    }
    // Combine headers if presented.
    if (headers) {
        Object.assign(options.headers, headers);
    }

    try {
        const response = await fetch(url, options);
        if (response.ok && onSuccessIsFunction) {
            if (!noResponseBody) {
                const responseBody = await response.json();
                onSuccess(responseBody);
            } else {
                onSuccess();
            }
        } else if (!response.ok && onErrorIsFunction) {
            onError(await response.text());
        }
        return response;
    } catch (exc) {
        return onErrorIsFunction ? onError(exc) : exc;
    }
}